'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import {
    Box,
    BoxContent,
    Icon,
    IconName,
    IconSize,
    Image,
    Spacer,
    SpacerSize,
    Title,
    TitleLevels,
    TitleMarkup,
} from '@bytel/trilogy-react';

import type { ConfigType } from '@app-types/api';
import { Connectivity, JOURNEYS, type PlanType } from '@app-types/product';

import { useHome } from '@landing/hooks';

import { CtaAddToCart } from './cta-add-to-cart';
import { DataList } from './data-list';
import { NetworkButtons } from './network-buttons';
import { BAndyouPrice } from './plan-header/b-and-you-price';
import { ProductDetail } from './product-detail';

function getHighlightedPlan(plans: PlanType[]): PlanType | undefined {
    return plans.find((plan) => plan.isHighlighted);
}

interface BAndYouConfiguratorProps {
    journey: JOURNEYS;
    plans: PlanType[];
    isMobileFirst: boolean;
    isLoading: boolean;
    config?: ConfigType;
}

export function BAndyouConfigurator({
    plans,
    isMobileFirst,
    isLoading,
    config,
    journey,
}: Readonly<BAndYouConfiguratorProps>) {
    const {
        selectedPlan: _selectedPlan,
        setSelectedPlan,
        temporarilySelectedPlan: _temporarilySelectedPlan,
        setTemporarilySelectedPlan,
        selectedOption,
    } = useHome();
    const searchParams = useSearchParams();

    const shouldShowCta = !isMobileFirst;

    const defaultPlan = getHighlightedPlan(plans) ?? plans[0]!;

    const selectedPlan = isMobileFirst ? (_selectedPlan ?? defaultPlan) : _selectedPlan;
    const temporarilySelectedPlan = _temporarilySelectedPlan ?? defaultPlan;

    const baseProduct = isMobileFirst && selectedPlan ? selectedPlan : temporarilySelectedPlan;

    const is5gOffer = !!(baseProduct?.connectivity === Connectivity.FIVE_G || selectedOption?.gencode);
    useEffect(() => {
        if (!plans[0]) {
            return;
        }

        const urlKeyParam = searchParams.get('url_key');
        const planFromUrl = plans.find((p) => p.url === urlKeyParam);

        if (planFromUrl) {
            setSelectedPlan(planFromUrl);
            setTemporarilySelectedPlan(planFromUrl);
        } else if (isMobileFirst) {
            setSelectedPlan(defaultPlan);
            setTemporarilySelectedPlan(null);
        } else {
            setSelectedPlan(null);
            setTemporarilySelectedPlan(defaultPlan);
        }
    }, [journey]);

    if (!plans || plans.length === 0) {
        return;
    }

    return (
        <Box data-testid='byou-container' id='plans-configurator' shadowless>
            <BAndyouPrice
                icon={
                    <>
                        <Icon name={is5gOffer ? IconName.UI_5G : IconName.UI_4G} size={IconSize.LARGE} />
                        {(selectedPlan?.planPicto?.length ?? 0) > 0 &&
                            selectedPlan?.planPicto?.map((picto) => (
                                <Image key={picto.public_url} src={picto.public_url} alt={picto.title} height={32} />
                            ))}
                    </>
                }
                isLoading={isLoading}
                baseProduct={baseProduct}
                config={config}
            />
            <BoxContent>
                <Title level={TitleLevels.FIVE} markup={TitleMarkup.H2}>
                    Quel {journey === JOURNEYS.PACKS ? 'pack' : 'forfait'} voulez-vous&nbsp;?
                </Title>
                <DataList plans={plans} isMobileFirst={isMobileFirst} selectedGencode={baseProduct?.gencode} />
                <div>
                    <NetworkButtons baseProduct={baseProduct ?? null} />
                    <Spacer size={SpacerSize.FOUR} />
                    <ProductDetail product={baseProduct ?? null} />
                </div>
                {shouldShowCta && (
                    <>
                        <Spacer size={SpacerSize.FIVE} />
                        <CtaAddToCart
                            selectedProduct={selectedPlan}
                            temporarilySelectedProduct={temporarilySelectedPlan}
                            handleAddToCart={() => {
                                if (temporarilySelectedPlan?.gencode !== selectedPlan?.gencode) {
                                    setSelectedPlan(temporarilySelectedPlan ?? null);
                                } else {
                                    setSelectedPlan(null);
                                }
                            }}
                            ctaLabel={{
                                productAdded: 'Retirer ce forfait',
                                productNotAdded: 'Ajouter ce forfait',
                                tagAdded: 'Forfait ajouté',
                            }}
                        />
                    </>
                )}
            </BoxContent>
        </Box>
    );
}
